import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { SPECS } from '@/content';
import RedO from '@/components/RedO';

export default function Specs() {
  const [openItem, setOpenItem] = useState('flooring');
  const [room, setRoom] = useState(SPECS.rooms[0]);

  return (
    <section id="specifications" data-testid="specs-section" className="scroll-mt-20 bg-white py-20 sm:py-28" aria-labelledby="specs-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <p className="eyebrow">{SPECS.eyebrow}</p>
        <h2 id="specs-heading" className="headline mt-3 text-3xl sm:text-5xl">
          <RedO text={SPECS.headline[0]} />
          <span className="mt-1 block"><RedO text={SPECS.headline[1]} /></span>
        </h2>

        {/* room gallery — swipeable */}
        <div className="scrollbar-none -mx-4 mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-4 sm:overflow-visible sm:px-0" data-testid="specs-gallery">
          {SPECS.rooms.map((r, i) => (
            <motion.button
              key={r.id}
              data-testid={`spec-room-${r.id}`}
              onClick={() => setRoom(r)}
              aria-pressed={room.id === r.id}
              className={`group relative w-[74%] shrink-0 snap-center overflow-hidden rounded-2xl border text-left transition-[border-color,transform] duration-300 sm:w-auto ${
                room.id === r.id ? 'border-[#C92F4B]' : 'border-[#E4E6EB]'
              }`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ delay: i * 0.08, duration: 0.55 }}
            >
              <img
                src={r.image}
                srcSet={`${r.imageMobile} 1080w, ${r.image} 2400w`}
                sizes="(max-width: 640px) 74vw, 25vw"
                alt={`Approved visual of the ${r.label.toLowerCase()} at Aarohi Complex`}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              />
              <div className="p-4">
                <p className="font-display text-sm font-extrabold uppercase tracking-[0.1em] text-[#1C1D21]">{r.label}</p>
                <p className="mt-1 text-[11px] leading-relaxed text-[#646A72]">{r.hint}</p>
              </div>
              {room.id === r.id && <span className="absolute right-3 top-3 h-3.5 w-3.5 rounded-full border-[3px] border-[#C92F4B] bg-white" aria-hidden="true" />}
            </motion.button>
          ))}
        </div>

        {/* spec accordion */}
        <div className="mt-10 divide-y divide-[#E4E6EB] rounded-3xl border border-[#E4E6EB] bg-[#FAF9F6]" data-testid="specs-list">
          {SPECS.items.map((s) => {
            const open = openItem === s.id;
            return (
              <div key={s.id}>
                <button
                  data-testid={`spec-item-${s.id}`}
                  aria-expanded={open}
                  onClick={() => setOpenItem(open ? null : s.id)}
                  className="flex min-h-[56px] w-full items-center justify-between gap-4 px-5 py-4 text-left sm:px-7"
                >
                  <span className="flex items-center gap-3">
                    <span className={`h-3 w-3 rounded-full border-2 transition-colors ${open ? 'border-[#C92F4B] bg-[#C92F4B]' : 'border-[#A7ABB0]'}`} aria-hidden="true" />
                    <span className="font-display text-sm font-extrabold uppercase tracking-[0.14em] text-[#1C1D21]">{s.label}</span>
                  </span>
                  <ChevronDown size={18} className={`shrink-0 text-[#646A72] transition-transform duration-300 ${open ? 'rotate-180 text-[#C92F4B]' : ''}`} />
                </button>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 pl-[52px] text-base leading-relaxed text-[#404145] sm:px-7 sm:pl-[60px]">{s.text}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
